import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import paymentService from "../services/paymentService";
import Sidebar from "../components/Sidebar";
import "../styles/Payments.css";
import { FaDollarSign, FaHourglassHalf, FaCheckCircle, FaLock } from "react-icons/fa";

function Payments() {
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState({
    ongoingQA: 0,
    availableToBeBilled: 0,
    totalExpectedPayout: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPaymentsData = async () => {
      if (!auth.currentUser) {
        setLoading(false);
        return;
      }

      try {
        // Fetch user's current payment from Firestore
        const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
        let currentMonthlyPayment = 0;

        if (userDoc.exists()) {
          const userData = userDoc.data();
          currentMonthlyPayment = userData.currentPayment || 0;
        }

        // Fetch payment history
        const paymentData = await paymentService.fetchMyPayments(auth.currentUser.uid);

        let totalPaid = 0;
        let totalPending = 0;

        if (paymentData) {
          // Sort by date (newest first)
          const sortedPayments = paymentData.sort((a, b) => {
            return new Date(b.date) - new Date(a.date);
          });
          setPayments(sortedPayments);

          // Calculate totals
          sortedPayments.forEach(payment => {
            if (payment.status === "Paid" || payment.status === "Released") {
              totalPaid += payment.amount || 0;
            } else if (payment.status === "Pending" || payment.status === "Pending QA") {
              totalPending += payment.amount || 0;
            }
          });
        }

        setStats({
          ongoingQA: totalPending,
          availableToBeBilled: currentMonthlyPayment,
          totalExpectedPayout: totalPaid + totalPending + currentMonthlyPayment
        });

      } catch (error) {
        console.error("Failed to fetch payments", error);
        setError("Failed to load payments data");
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentsData();
  }, []);

  const getStatusClass = (status) => {
    switch (status) {
      case "Paid":
      case "Released":
        return "status-released";
      case "Processing":
      case "Pending QA":
        return "status-pending-qa";
      case "Pending":
        return "status-pending";
      default:
        return "status-pending";
    }
  };

  if (loading) {
    return (
      <div className="payments-page">
        <Sidebar />
        <div className="payments-container">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payments-page">
        <Sidebar />
        <div className="payments-container">
          <div className="payments-header">
            <h1 className="payments-title">Payments Overview</h1>
          </div>
          <p style={{ color: 'var(--text-secondary)' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="payments-page">
      <Sidebar />
      <div className="payments-container">
        <div className="payments-header">
          <h1 className="payments-title">Payments Overview</h1>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '12px' }}>
            <div style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid #3B82F6',
              color: '#3B82F6',
              padding: '8px 16px',
              borderRadius: '8px',
              fontWeight: '600',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FaHourglassHalf /> Payment Cycle: Every Thursday
            </div>

           
          </div>
        </div>

        <div className="payments-stats-grid">
          <div className="payment-stat-card">
            <h3 className="stat-card-title">Ongoing QA</h3>
            <p className="stat-card-amount">₹{stats.ongoingQA.toLocaleString()}</p>
            <span className="stat-card-badge pending">Pending Approval</span>
          </div>

          <div className="payment-stat-card">
            <h3 className="stat-card-title">Available to be Billed</h3>
            <p className="stat-card-amount">₹{stats.availableToBeBilled.toLocaleString()}</p>
            <span className="stat-card-badge released">Will Be Released</span>
          </div>

          <div className="payment-stat-card">
            <h3 className="stat-card-title">Total Amount</h3>
            <p className="stat-card-amount">₹{stats.totalExpectedPayout.toLocaleString()}</p>
          </div>
        </div>

        <div className="payment-history-section">
          <h2 className="section-title">Payment History</h2>
          {payments.length === 0 ? (
            <div className="no-history">
              <p>No payment history available</p>
            </div>
          ) : (
            <table className="payment-history-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment, index) => (
                  <tr key={payment._id || index}>
                    <td>{payment.description || "N/A"}</td>
                    <td>{new Date(payment.date).toLocaleDateString()}</td>
                    <td>₹{payment.amount.toLocaleString()}</td>
                    <td>
                      <span className={`status-badge ${getStatusClass(payment.status)}`}>
                        {payment.status}
                      </span>
                    </td>
                    <td>-</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default Payments;